import {DotProgress} from '@wandb/components';
import {DiffEditor, Editor} from '@wandb/weave/common/components/Monaco/react';
import {type StructuredPatch} from 'diff';
import React, {useState} from 'react';

import {fileInfoFromName} from '../fileInfoFromName';
import {MonacoWrapper} from './MonacoWrapper';
import {PatchError} from './PatchError';
import {getFileName} from './patchFileUtils';
import type {EditorType, RenderMode} from './types';
import {LINE_HEIGHT, useMonacoOptions} from './useMonacoOptions';
import {usePatchFile} from './usePatchFile';
import {usePatchLineCount} from './usePatchLineCount';
import {DIFF_LANG, usePatchViewerTheme} from './usePatchViewerTheme';

const LOADER = (
  <div className="flex items-center justify-center h-64">
    <DotProgress aria-label="Loading patch file..." />
  </div>
);

type StructuredPatchViewerProps = {
  artifactId: string;
  patch: StructuredPatch;
  renderMode: RenderMode;
};

export function StructuredPatchViewer({
  artifactId,
  patch,
  renderMode,
}: StructuredPatchViewerProps) {
  const parsedPatch = usePatchFile(artifactId, patch);
  const theme = usePatchViewerTheme();
  const [editorInstance, setEditorInstance] = useState<EditorType | null>(null);
  const lineCount = usePatchLineCount(editorInstance, renderMode);
  const options = useMonacoOptions(renderMode);

  const fileInfo = fileInfoFromName(getFileName(patch));
  const height = Math.max(lineCount * LINE_HEIGHT + LINE_HEIGHT, 64);

  if (parsedPatch.status === 'loading') {
    return LOADER;
  }
  if (parsedPatch.status === 'error') {
    return (
      <MonacoWrapper>
        {/* Only render PatchError here because if the state == error, the
            side-by-side diff won't be renderable. */}
        <PatchError fileInfo={fileInfo} patchState={parsedPatch} />
        <Editor
          language={DIFF_LANG}
          value={parsedPatch.content}
          height={height}
          onMount={setEditorInstance}
          theme={theme}
          options={options}
        />
      </MonacoWrapper>
    );
  }

  return (
    <MonacoWrapper>
      <DiffEditor
        height={height}
        language={fileInfo.language ?? 'text'}
        loading={LOADER}
        original={parsedPatch.originalContent}
        modified={parsedPatch.modifiedContent}
        onMount={setEditorInstance}
        theme={theme}
        options={options}
      />
    </MonacoWrapper>
  );
}
